import React, { useRef, useState } from 'react';
import { Input, Spin } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { Customization } from './types';
import {
  CheckIconWrap,
  DropdownEmptyMsg,
  DropdownLoadingWrap,
  SearchWrapper,
  SelectedBoxEmpty,
  SelectedBoxLabel,
  SelectedItemText,
  SelectedTagClose,
  SelectedTagItem,
  SelectedTagsBox,
  SuggestionItem,
  SuggestionsDropdown,
  TagsWrap,
} from './styles';

interface CustomizationsSectionProps {
  allCustomizations: Customization[];
  selected: Customization[];
  onChange: (selected: Customization[]) => void;
  isDarkMode: boolean;
  isLoading?: boolean;
}

const CustomizationsSection: React.FC<CustomizationsSectionProps> = ({
  allCustomizations,
  selected,
  onChange,
  isDarkMode,
  isLoading,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [show, setShow] = useState(false);

  const filtered = query.trim()
    ? allCustomizations.filter((c) =>
        c.customization.toLowerCase().includes(query.toLowerCase())
      )
    : allCustomizations;

  const handleToggle = (c: Customization) => {
    if (selected.some((s) => s.id === c.id)) {
      onChange(
        selected
          .filter((s) => s.id !== c.id)
          .map((s, i) => ({ ...s, sequence_number: i.toString() }))
      );
    } else {
      const maxSeq = selected.reduce((max, s) => {
        const n = parseInt(s.sequence_number as string, 10);
        return !isNaN(n) && n > max ? n : max;
      }, -1);
      onChange([...selected, { ...c, sequence_number: (maxSeq + 1).toString() }]);
    }
  };

  const handleSelectAll = () => {
    if (selected.length === allCustomizations.length) {
      onChange([]);
    } else {
      onChange(allCustomizations.map((c, i) => ({ ...c, sequence_number: i.toString() })));
    }
  };

  const handleRemove = (id: number | string) => {
    onChange(
      selected
        .filter((s) => s.id !== id)
        .map((s, i) => ({ ...s, sequence_number: i.toString() }))
    );
  };

  return (
    <>
      <SearchWrapper ref={wrapperRef}>
        <Input
          placeholder="Search customizations..."
          value={query}
          allowClear
          suffix={isLoading ? <Spin size="small" /> : undefined}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setShow(true)}
          onBlur={() => setTimeout(() => setShow(false), 150)}
        />
        {show && (
          <SuggestionsDropdown $isDark={isDarkMode}>
            {isLoading ? (
              <DropdownLoadingWrap>
                <Spin size="small" />
              </DropdownLoadingWrap>
            ) : filtered.length === 0 ? (
              <DropdownEmptyMsg>
                {query.trim() ? 'No results found.' : 'No customizations available.'}
              </DropdownEmptyMsg>
            ) : (
              <>
                <SuggestionItem
                  $isDark={isDarkMode}
                  $isSelectAll
                  onMouseDown={(e) => { e.preventDefault(); handleSelectAll(); }}
                >
                  {selected.length === allCustomizations.length ? 'Unselect All' : 'Select All'}
                </SuggestionItem>
                {filtered.map((c) => {
                  const isChecked = selected.some((s) => s.id === c.id);
                  return (
                    <SuggestionItem
                      key={c.id}
                      $isDark={isDarkMode}
                      onMouseDown={(e) => { e.preventDefault(); handleToggle(c); }}
                    >
                      {isChecked
                        ? <SelectedItemText>{c.customization}</SelectedItemText>
                        : <span>{c.customization}</span>}
                      {isChecked && (
                        <CheckIconWrap><CheckOutlined /></CheckIconWrap>
                      )}
                    </SuggestionItem>
                  );
                })}
              </>
            )}
          </SuggestionsDropdown>
        )}
      </SearchWrapper>

      <SelectedTagsBox $isDark={isDarkMode}>
        <SelectedBoxLabel $isDark={isDarkMode}>Selected Modifier Groups:</SelectedBoxLabel>
        {selected.length === 0 ? (
          <SelectedBoxEmpty $isDark={isDarkMode}>No modifier groups selected.</SelectedBoxEmpty>
        ) : (
          <TagsWrap>
            {[...selected]
              .sort(
                (a, b) =>
                  parseInt(a.sequence_number as string, 10) -
                  parseInt(b.sequence_number as string, 10)
              )
              .map((c) => (
                <SelectedTagItem key={c.id}>
                  {c.customization || 'unnamed customization'}
                  <SelectedTagClose onClick={() => handleRemove(c.id)}>✕</SelectedTagClose>
                </SelectedTagItem>
              ))}
          </TagsWrap>
        )}
      </SelectedTagsBox>
    </>
  );
};

export default CustomizationsSection;
